import React from 'react';

const ConstructionismPage = () => {
  return (
    <div className="App">

      <section className="section" style={{ backgroundColor: 'var(--light)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Constructionism</h2>
            <p>Learning through building physical objects</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '3fr 2fr',
            gap: '3rem',
            alignItems: 'start'
          }}>
            <div>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1.5rem' }}>The Constructionist Approach</h3>

              <p style={{ marginBottom: '1rem' }}>
                Constructionism, developed by MIT mathematician Seymour Papert in the 1980s, builds upon constructivist learning theories
                by emphasizing the creation of physical artifacts as a key learning process. While constructivism (developed by Jean Piaget)
                proposes that people construct knowledge through experience, constructionism adds that learning is particularly effective
                when people create tangible objects in the real world.
              </p>

              <p style={{ marginBottom: '1rem' }}>
                Papert, who worked closely with Piaget, observed that when children built meaningful projects, they simultaneously
                constructed knowledge in their minds. This insight led to the development of the LOGO programming language and
                the famous "turtle" robots that allowed children to learn mathematical concepts through programming physical movement.
              </p>

              {/* <div style={{
                padding: '1.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                margin: '2rem 0',
                borderLeft: '4px solid var(--secondary)'
              }}>
                <p style={{
                  fontStyle: 'italic',
                  fontSize: '1.1rem',
                  color: 'var(--dark)',
                  marginBottom: '1rem'
                }}>
                  "The simplest definition of constructionism evokes the idea of learning-by-making... We understand 'constructionism'
                  as including, but going beyond, what Piaget would call 'constructivism.' The word with the v expresses the theory
                  that knowledge is built by the learner, not supplied by the teacher. The word with the n expresses the further
                  idea that this happens especially felicitously when the learner is engaged in the construction of something
                  external or at least shareable."
                </p>
                <p style={{ textAlign: 'right', fontWeight: '500' }}>—Seymour Papert</p>
              </div> */}

              <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1.5rem' }}>
                Constructionism in Nozomi Robotics
              </h3>

              <p style={{ marginBottom: '1rem' }}>
                Nozomi Robotics is deeply rooted in constructionist principles. Our approach incorporates several key elements of
                constructionism:
              </p>

              <ul style={{
                listStyleType: 'none',
                padding: '0',
                margin: '1.5rem 0'
              }}>
                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Physical Construction:</strong> Users physically build their robots from component parts,
                  creating a tangible embodiment of their ideas.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Personally Meaningful Projects:</strong> Our challenges encourage users to create robots that solve
                  problems that interest them, making the learning experience more engaging.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Low Floor, High Ceiling:</strong> The block-based programming interface makes it easy to get started
                  (low floor) while allowing for increasingly complex projects over time (high ceiling).
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Debugging as Learning:</strong> When robots don't behave as expected, users engage in a powerful
                  learning process as they troubleshoot and refine their understanding.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Shareable Creations:</strong> The projects built with Nozomi Robotics are inherently shareable, allowing
                  users to demonstrate and explain their work to others, reinforcing their learning.
                </li>
              </ul>

              <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1.5rem' }}>
                Research Evidence
              </h3>
              
              <div style={{
                marginTop: '2.5rem',
                padding: '1.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: '4px solid var(--primary)'
              }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--dark)' }}>Key Research References:</h4>
                <ul style={{ fontSize: '0.9rem', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Papert, S. (1980). Mindstorms: Children, computers, and powerful ideas. Basic Books.
                  </li>
                  {/* <li style={{ marginBottom: '0.5rem' }}>
                    Resnick, M., et al. (2009). Scratch: Programming for all. Communications of the ACM, 52(11), 60-67.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Bers, M. U. (2018). Coding as a playground: Programming and computational thinking in the early childhood classroom. Routledge.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Blikstein, P. (2013). Digital fabrication and 'making' in education: The democratization of invention. In J. Walter-Herrmann & C. Büching (Eds.), FabLabs: Of machines, makers and inventors. Bielefeld: Transcript Publishers.
                  </li>
                  <li>
                    Martin, L. (2015). The promise of the maker movement for education. Journal of Pre-College Engineering Education Research, 5(1), 30-39.
                  </li> */}
                </ul>
              </div>
            </div>

            <div>
              <div style={{
                position: 'sticky',
                top: '100px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                padding: '1.5rem',
                borderTop: '4px solid var(--secondary)'
              }}>
                <h4 style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--dark)' }}>Constructionism in Action</h4>

                {/* <img
                  src="/api/placeholder/400/320"
                  alt="Constructionism Learning Diagram"
                  style={{ width: '100%', height: 'auto', borderRadius: '4px', marginBottom: '1rem' }}
                /> */}

                <p style={{
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  color: '#666',
                  fontStyle: 'italic',
                  marginBottom: '1.5rem'
                }}>
                  The constructionist learning cycle in Nozomi Robotics
                </p>

                <div style={{
                  backgroundColor: 'var(--light)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <h5 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>The Spiral Curriculum</h5>
                  <p style={{ fontSize: '0.9rem' }}>
                    Nozomi Robotics adopts Papert's spiral approach, where learners revisit the same concepts with increasing
                    complexity as their skills develop.
                  </p>
                </div>

                <div style={{
                  backgroundColor: 'var(--light)',
                  padding: '1rem',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>The Power of Tinkering</h5>
                  <p style={{ fontSize: '0.9rem' }}>
                    Our platform encourages experimentation and iteration, allowing users to develop intuitive understandings
                    of complex concepts through hands-on exploration.
                  </p>
                </div>
              </div>

              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                padding: '1.5rem',
                marginTop: '2rem',
                borderTop: '4px solid var(--accent)'
              }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--dark)' }}>Constructionist Practices</h4>
                <p style={{ marginBottom: '1rem' }}>
                  Ways to implement constructionist principles in your learning environment:
                </p>

                <ol style={{ paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.8rem' }}>
                    Prioritize project-based learning
                  </li>
                  <li style={{ marginBottom: '0.8rem' }}>
                    Allow time for exploration and discovery
                  </li>
                  <li style={{ marginBottom: '0.8rem' }}>
                    Celebrate the process, not just the product
                  </li>
                  <li style={{ marginBottom: '0.8rem' }}>
                    Create opportunities for reflection
                  </li>
                  <li>
                    Build a culture where errors are valuable learning opportunities
                  </li>
                </ol>

                <div style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--light)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <a href="#" className="cta-button" style={{ display: 'inline-block' }}>
                    Educator's Guide to Constructionism
                  </a>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ConstructionismPage;